import { formatNumber } from '@angular/common';
import { Directive, EventEmitter, inject, Input, LOCALE_ID, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { InstrumentType } from '@zero/shared/util-models';
import { LegendPaging, StatisticsFacade, Weighting, WeightingType } from '@zero/statistics/domain';
import { ChartConfiguration } from 'chart.js';

@Directive()
export class WeightingBase implements OnInit, OnChanges {
  @Input() public weighting: Weighting;
  @Input() public loading: boolean;
  @Input() public type: WeightingType;

  @Output() public weightingTypeChange = new EventEmitter<WeightingType>();

  readonly #translateService = inject(TranslocoService);
  readonly #localId = inject(LOCALE_ID);
  protected statisticsFacade = inject(StatisticsFacade);

  public config: ChartConfiguration<'doughnut'>['data']['datasets'];
  public legendLabels: string[];
  public legendPaging: LegendPaging;
  public colors: string[];

  public readonly WeightingType = WeightingType;
  public readonly options: ChartConfiguration<'doughnut'>['options'] = {
    responsive: true,
    plugins: {
      tooltip: {
        enabled: true,
        callbacks: {
          title: (tooltipItem) => this.#translateService.translate(this.legendLabels[tooltipItem[0].dataIndex]),
          label: (tooltipItem) => `${formatNumber(Number(tooltipItem.raw), this.#localId, '1.2-2')} €`,
          afterLabel: (tooltipItem) => {
            try {
              return `${formatNumber((Number(tooltipItem.raw) / this.weighting.totalVolumesSum) * 100, this.#localId, '1.2-2')} %`;
            } catch (e) {
              return '';
            }
          },
        },
      },
    },
  };
  readonly #displayLegendItems = 4;

  ngOnInit(): void {
    this.colors = [
      '#248eff',
      '#02172D',
      '#E0146A',
      '#D3C398',
      '#093967',
      '#aa074c',
      '#99b2cd',
      '#87774e',
      '#0bae49',
      '#c80808',
      ...this.#getRandomColors(),
    ];
  }

  public ngOnChanges(changes: SimpleChanges): void {
    changes['weighting']?.currentValue && this.#init();
  }

  #getRandomColors = (): string[] => {
    const colors = [];
    for (let i = 0; i < 1000; i++) {
      colors.push(`#${(Math.random() * 0xfffff * 1000000).toString(16).slice(0, 6)}`);
    }
    return colors;
  };

  public useLegendPaging = (index: number): void => {
    this.legendPaging.current = index;
    this.legendPaging.from = index * this.#displayLegendItems;
    this.legendPaging.to = this.legendPaging.from + this.#displayLegendItems;
  };

  #init = (): void => {
    this.config = [
      {
        data: this.#getChartData(),
        backgroundColor: this.colors,
        borderWidth: 0,
      },
    ];
    this.#setLegend();
    this.#setPaging();
  };

  #getChartData = (): number[] => {
    const isAssetTypeMode = this.type === WeightingType.ASSETS;
    const data = (
      isAssetTypeMode
        ? Object.values(InstrumentType)
            .filter((type) => Boolean(this.weighting.volumesPerAssetType[type]))
            .map((type) => this.weighting.volumesPerAssetType[type])
        : this.weighting.positions.map(({ volume }) => volume)
    ).sort((a, b) => b - a);

    !isAssetTypeMode && this.weighting.remainingVolumesSum && data.push(this.weighting.remainingVolumesSum);
    return data;
  };

  #setLegend = () => {
    const isAssetTypeMode = this.type === WeightingType.ASSETS;
    this.legendLabels = [];
    this.config[0]['data'].forEach((item) => {
      if (isAssetTypeMode) {
        Object.values(InstrumentType)
          .filter((type) => this.weighting.volumesPerAssetType[type] === item)
          .forEach((type) => this.legendLabels.push(`statistics.weighting.labels.${type}`));
      } else {
        this.weighting.positions
          .filter((position) => position.volume === item)
          .forEach((position) => this.legendLabels.push(position.assetName));
      }
    });

    !isAssetTypeMode && this.weighting.remainingVolumesSum && this.legendLabels.push(`statistics.weighting.labels.others`);
  };

  #setPaging = () => {
    if (this.config[0]['data']?.length <= this.#displayLegendItems) {
      this.legendPaging = null;
      return;
    }

    this.legendPaging = {
      items: Math.ceil(this.config[0]['data'].length / this.#displayLegendItems),
      from: 0,
      to: this.#displayLegendItems,
      current: 0,
    };
  };
}
